/**
 * Lead Service
 * Handles all operations related to Leads (Contact Requests)
 */
const LeadService = {
    /**
     * Get all leads
     */
    getLeads() {
        return DataManager.getAll(DataManager.KEYS.LEADS) || [];
    },

    /**
     * Get lead by ID
     */
    getLead(id) {
        return DataManager.getById(DataManager.KEYS.LEADS, id);
    },

    // Cache only the canonical record after a successful authenticated write.
    async persist(method, id, data) {
        const epoch = AuthManager._epoch;
        const allowed = method === 'DELETE' ? AuthManager.isAdmin() : ['admin', 'coordinator'].includes(AuthManager.currentUser?.role);
        if (!allowed) throw new Error('No tienes permisos para modificar contactos.');
        const response = await fetch('/api/leads' + (id ? '/' + encodeURIComponent(id) : ''), {
            method, headers: {'Content-Type':'application/json', 'X-SICAF-CSRF':AuthManager.getCsrfToken()},
            ...(data ? {body:JSON.stringify(data)} : {})
        });
        const result = await response.json().catch(() => null);
        if (epoch !== AuthManager._epoch) throw new Error('La sesión ha cambiado. Vuelve a entrar.');
        if (!response.ok || !result?.success || (method !== 'DELETE' && !result.lead?.id)) {
            throw new Error(result?.error || 'No se pudo guardar el contacto.');
        }
        this._searches.clear();
        const rows = this.getLeads().filter(lead => lead.id !== (id || result.lead.id));
        if (method !== 'DELETE') rows.unshift(result.lead);
        FileStorage.dataStore.leads = rows;
        BrowserState.setItem(DataManager.KEYS.LEADS, JSON.stringify(rows));
        return result.lead || result;
    },

    createLead(data) { return this.persist('POST', null, data); },

    // Search boxes (header and Buscar): contacts come from the server, ten at most, because the
    // browser no longer holds every contact. Returns the cached answer, or null while it is on
    // its way (onReady runs when it arrives, so the caller repaints).
    _searches: new Map(),
    canSearch() {
        return ['admin', 'coordinator', 'rrhh'].includes(AuthManager.currentUser?.role) || AuthManager.hasModule('leads');
    },
    searchCached(query, onReady) {
        const key = String(query || '').trim().toLowerCase();
        if (key.length < 2 || !this.canSearch()) return [];
        if (this._searches.has(key)) return this._searches.get(key);
        if (this._searches.size > 50) this._searches.clear();
        this._searches.set(key, null);
        fetch('/api/leads?q=' + encodeURIComponent(key), { cache: 'no-store' })
            .then(response => response.ok ? response.json() : null)
            .then(body => { this._searches.set(key, body?.leads || []); if (typeof onReady === 'function') onReady(); })
            .catch(() => this._searches.set(key, []));
        return null;
    },

    // Contactos CRM → Configuración: web-form text → SICAF formation.
    async interestRequest(method, id, data) {
        const response = await fetch('/api/leads/interest-mappings' + (id ? '/' + encodeURIComponent(id) : ''), {
            method, headers: {'Content-Type': 'application/json', 'X-SICAF-CSRF': AuthManager.getCsrfToken()},
            cache: 'no-store', ...(data ? {body: JSON.stringify(data)} : {})
        });
        const result = await response.json().catch(() => null);
        if (!response.ok || !result?.success) throw new Error(result?.error || 'No se pudo completar la operación.');
        return result;
    },
    getInterestMappings() { return this.interestRequest('GET'); },
    saveInterestMapping(id, data) { return this.interestRequest(id ? 'PUT' : 'POST', id, data); },
    deleteInterestMapping(id) { return this.interestRequest('DELETE', id); },

    // Contacts of one target year ('all' only when chosen) and the years that have contacts:
    // the browser never loads the whole list by default.
    async refreshLeads(year) {
        const response = await fetch(`/api/leads?year=${encodeURIComponent(year)}&withYears=1`, {headers: {'X-SICAF-CSRF': AuthManager.getCsrfToken()}, cache: 'no-store'});
        const result = await response.json().catch(() => null);
        if (!response.ok || !Array.isArray(result?.leads)) throw new Error('No se pudieron actualizar los contactos.');
        this._searches.clear();
        FileStorage.dataStore.leads = result.leads;
        BrowserState.setItem(DataManager.KEYS.LEADS, JSON.stringify(result.leads));
        return { leads: result.leads, years: Array.isArray(result.years) ? result.years : [] };
    },

    /** Contacts that arrived between two days (YYYY-MM-DD), whatever the year shown: campaigns by dates. */
    async fetchRange(from, to) {
        const params = new URLSearchParams();
        if (from) params.set('from', from);
        if (to) params.set('to', to);
        const response = await fetch('/api/leads?' + params, {headers: {'X-SICAF-CSRF': AuthManager.getCsrfToken()}, cache: 'no-store'});
        const result = await response.json().catch(() => null);
        if (!response.ok || !Array.isArray(result?.leads)) throw new Error('No se pudieron cargar los contactos de esas fechas.');
        return result.leads;
    },
    updateLead(id, data) { return this.persist('PUT', id, data); },
    deleteLead(id) { return this.persist('DELETE', id); },

    /**
     * Send bulk email to leads
     */
    async sendBulkEmail(leadIds, subject, bodyTemplate) {
        const token = AuthManager.getCsrfToken();
        const response = await fetch('/api/leads/email', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-SICAF-CSRF': token
            },
            body: JSON.stringify({ leadIds, subject, bodyTemplate })
        });

        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Error al enviar correos masivos');
        }

        return await response.json();
    },

    // Recent campaigns with their progress (sent, failed, skipped, pending).
    async getCampaigns() {
        const response = await fetch('/api/email-campaigns', { headers: { 'X-SICAF-CSRF': AuthManager.getCsrfToken() }, cache: 'no-store' });
        if (!response.ok) return [];
        return (await response.json()).campaigns || [];
    },

    async cancelCampaign(id) {
        const response = await fetch('/api/email-campaigns/' + encodeURIComponent(id) + '/cancel', { method: 'POST', headers: { 'X-SICAF-CSRF': AuthManager.getCsrfToken() } });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.error || 'No se pudo cancelar la campaña');
        return body;
    },

    // One-off invitation to visit the school; the server records the date and keeps the
    // contact's list entry in sync.
    async sendVisitInvitation(leadId, subject, bodyTemplate) {
        const response = await fetch('/api/leads/' + encodeURIComponent(leadId) + '/visit-invitation', {
            method: 'POST', headers: {'Content-Type': 'application/json', 'X-SICAF-CSRF': AuthManager.getCsrfToken()},
            body: JSON.stringify({subject, bodyTemplate})
        });
        const result = await response.json().catch(() => null);
        if (!response.ok || !result?.success) throw new Error(result?.error || 'No se pudo enviar la invitación.');
        const leads = this.getLeads().map(lead => lead.id === leadId ? result.lead : lead);
        FileStorage.dataStore.leads = leads;
        BrowserState.setItem(DataManager.KEYS.LEADS, JSON.stringify(leads));
        return result.lead;
    },

    /**
     * Get saved email template by type from server
     * Returns { subject, body } or null if no saved template exists
     */
    async getEmailTemplate(templateType) {
        const token = AuthManager.getCsrfToken();
        try {
            const response = await fetch(`/api/settings/email-templates/${templateType}`, {
                headers: { 'X-SICAF-CSRF': token }
            });
            if (!response.ok) return null;
            const result = await response.json();
            return result.template || null;
        } catch (e) {
            return null;
        }
    },

    /**
     * Save (or overwrite) an email template in GlobalSettings
     */
    async saveEmailTemplate(templateType, subject, body) {
        const token = AuthManager.getCsrfToken();
        const response = await fetch('/api/settings/email-templates', {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'X-SICAF-CSRF': token
            },
            body: JSON.stringify({ templateType, subject, body })
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Error al guardar la plantilla');
        }
        return await response.json();
    }
};

window.LeadService = LeadService;
